// utils/currencyInput.js
import { useState, useCallback } from "react";

/**
 * Clean and normalize raw input string for currency entry.
 * - removes invalid chars
 * - allows only one decimal point
 * - limits decimals to maxDecimals
 * - replaces leading "0" with new digit if user types non-zero
 *
 * Returns the normalized raw value (string), suitable to store in state.
 */
export function normalizeCurrencyInput(raw, options = {}) {
    const { maxDecimals = 2, prevValue = "" } = options;

    if (raw == null) return "";

    // Keep only digits and dot
    let s = raw.toString();

    // Allow if user typed only '.' -> interpret as '0.'
    if (s === ".") return "0.";

    // Remove everything except digits and dot
    s = s.replace(/[^\d.]/g, "");

    // If more than one dot, keep first and remove the rest
    const firstDotIndex = s.indexOf(".");
    if (firstDotIndex >= 0) {
        // keep digits before dot, one dot, and digits after (trimmed to maxDecimals)
        const before = s.slice(0, firstDotIndex).replace(/^0+(?=\d)/, ""); // strip leading zeros except for single zero
        let after = s.slice(firstDotIndex + 1).replace(/\./g, ""); // remove other dots if any
        after = after.slice(0, maxDecimals);
        // If user typed ".5" we want "0.5"
        const finalBefore = before === "" ? "0" : before;
        return `${finalBefore}.${after}`;
    } else {
        // no decimal point
        // If previous value was "0" and user typed a non-zero digit, replace "0" with digit.
        // If user intentionally types multiple leading zeros, keep single "0"
        if (prevValue === "0" && s.length > 0) {
            // if they typed "0" again -> keep "0". if they typed '5' -> become '5'
            if (s === "0") return "0";
            // remove leading zeros then keep typed digits
            const withoutLeadingZeros = s.replace(/^0+/, "");
            return withoutLeadingZeros === "" ? "0" : withoutLeadingZeros;
        }

        // Strip leading zeros except keep single '0' if all zeros
        const stripped = s.replace(/^0+(?=\d)/, "");
        return stripped === "" ? "0" : stripped;
    }
}

/**
 * Format a numeric string to Indian locale with fixed decimals for display on blur.
 * Pass a string like "1234.5" -> "₹1,234.50"
 * If the value is empty or not a number, shows "₹0.00"
 */
export function formatCurrencyForDisplay(value, options = {}) {
    const { maxDecimals = 2 } = options;
    const num = Number(value);
    if (!isFinite(num)) {
        return `₹0.00`;
    }
    // Use minimumFractionDigits to ensure .00
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: maxDecimals, maximumFractionDigits: maxDecimals })}`;
}

/**
 * Hook: useCurrencyInput
 * - value: controlled value (string) and setter
 * - returns props for TextField: value, onChange, onBlur, onFocus
 */
export function useCurrencyInput(value, setValue, opts = {}) {
    const { maxDecimals = 2 } = opts;

    // onChange receives the raw event target.value (what the user types)
    const onChange = useCallback(
        (e) => {
            const raw = e.target.value;
            const normalized = normalizeCurrencyInput(raw, { maxDecimals, prevValue: value ?? "" });
            setValue(normalized);
        },
        [maxDecimals, setValue, value]
    );

    // onBlur: format to locale string if you want, but since we show ₹ via adornment,
    // here we just ensure decimal places if user left fewer than required decimals.
    const onBlur = useCallback(
        (e) => {
            let v = value ?? "";
            // If user input ends with '.' -> keep the 0 after dot
            if (v.endsWith(".")) {
                v = v.slice(0, -1);
            }
            // Ensure at least 0 if empty
            if (v === "" || v == null) v = "0";
            // If there is a decimal part, pad it to maxDecimals
            if (v.indexOf(".") >= 0) {
                const [intPart, decPart = ""] = v.split(".");
                const paddedDec = decPart.padEnd(maxDecimals, "0").slice(0, maxDecimals);
                setValue(`${intPart}.${paddedDec}`);
            } else {
                // no decimal -> add .00
                setValue(`${v}.${"0".repeat(maxDecimals)}`);
            }
        },
        [maxDecimals, setValue, value]
    );

    // onFocus: when focused, remove locale formatting (we don't format on change, but ensure there's no ₹ in value)
    const onFocus = useCallback(
        (e) => {
            // do nothing special: keep raw numeric string
            // But ensure that if value is '0.00' we show '0' so user can simply type a digit and replace it
            const v = value ?? "";
            if (v === "" || /^0+(\.0*)?$/.test(v)) {
                setValue("0");
            }
        },
        [value, setValue]
    );

    // displayValue: while typing we want to show the raw numeric string
    const displayValue = value ?? "0";

    return { displayValue, onChange, onBlur, onFocus, maxDecimals };
}